import React from 'react';
import PropTypes from 'prop-types';
import {
    HashRouter, Route, Switch, Redirect, withRouter
} from 'react-router-dom';
import ObjectHelper from 'foundation/object-helper';
import ArrayHelper from 'foundation/array-helper';
import UrlHelper from 'foundation/url-helper';
import contentBlocker from '../../../general/js/content-blocker';
import overlay from '../../../general/js/overlay';
import eventBus from '../../../general/js/event-bus';
import SelectDaysPage from './select-days-page';
import SelectTicketsPage from './select-tickets-page';
import SelectExtrasPage from './select-extras-page';
import SelectParkingPage from './select-parking-page';
import SelectParkingStandalonePage from './select-parking-standalone-page';
import ValidationHelper from '../../../general/js/basket/validation-helper';
import { TicketBuilderContext } from './ticket-builder-context';

import { EVENT_DEVICE_TYPE_CHANGED } from '../../../general/js/app';
import {
    PRODUCT_TYPE_TICKET,
    PRODUCT_TYPE_HOSPITALITY,
    PRODUCT_TYPE_PARKING, 
    PRODUCT_TYPE_EXTRAS,
    PRODUCT_TAG_TICKET_PLUS,
    PRODUCT_TAG_TICKET_PLUS_EXTRA
} from '../../../general/js/basket/constants';
import {
    ROUTE_DAYS,
    ROUTE_EXTRAS,
    ROUTE_PARKING,
    ROUTE_TICKETS,
    ROUTE_PARKING_STANDALONE,
    SCENARIO_HOSPITALITY,
    SCENARIO_STANDARD,
    SCENARIO_PARKING,
    SCENARIO_HOSPITALITY_WITH_EXTRAS,
    SCENARIO_STANDARD_WITH_EXTRAS,
    SCENARIO_PARKING_STANDALONE
} from './constants';

import { 
    isNullOrUndefined 
} from '../../../general/js/general-helpers';

const PRODUCT_GROUP_ID_URL_PARAM_NAME = 'g';

class TicketBuilder extends React.Component {
    constructor(props) {
        super(props);

        this.hasPreselectedProductGroup = false;
        this.currentProductType = this.getCurrentProductType();

        this.state = {
            productGroupsList: this.getSelectedProductGroups(),
            isBasketExpanded: false,
            isBasketToggling: false,
        };

        this.setHasPreselectedProductGroup();

        if (!Object.keys(this.state.productGroupsList).length) {
            window.location.hash = '';
        }

        this.onSelectProductGroup = this.onSelectProductGroup.bind(this);
        this.onApplyProductsQuantity = this.onApplyProductsQuantity.bind(this);
        this.onClearSelection = this.onClearSelection.bind(this);
        this.onToggleBasketMobile = this.onToggleBasketMobile.bind(this);

        eventBus.addListener(EVENT_DEVICE_TYPE_CHANGED, this.updateBasketDesign);

        this.tbContext = {
            currentScenarioTitle: this.getCurrentScenarioTitle()
        };
    }

    componentDidUpdate(prevProps) {
        // scroll to top on every new page
        if (this.props.location !== prevProps.location) {
            window.scrollTo(0, 0);
        }

        // we have to reset this flag to prevent another page redirect
        this.hasPreselectedProductGroup = false;
    }

    componentWillUnmount() {
        eventBus.removeListener(EVENT_DEVICE_TYPE_CHANGED, this.updateBasketDesign);
    }

    updateBasketDesign = () => {
        if (this.state.isBasketExpanded) {
            this.onToggleBasketMobile();
        }
    }

    getCurrentScenarioTitle() {
        switch (this.props.currentScenario) {
            case SCENARIO_STANDARD:
                return 'Buy Tickets';
            case SCENARIO_HOSPITALITY:
            case SCENARIO_HOSPITALITY_WITH_EXTRAS:
                return this.props.categoriesList[0].title;
            case SCENARIO_STANDARD_WITH_EXTRAS:
                return 'Buy Tickets';
            case SCENARIO_PARKING:
                return 'Buy Parking';
            case SCENARIO_PARKING_STANDALONE:
                    return 'Buy Parking Standalone';    
            default:
                return 'Buy Products';
        }
    }

    getCurrentProductType() {
        let productType = '';

        switch (this.props.currentScenario) {
            case SCENARIO_STANDARD:
                productType = PRODUCT_TYPE_TICKET;
                break;
            case SCENARIO_HOSPITALITY:
            case SCENARIO_HOSPITALITY_WITH_EXTRAS:
                productType = PRODUCT_TYPE_HOSPITALITY;
                break;
            case SCENARIO_PARKING:
                productType = PRODUCT_TYPE_PARKING;
                break;
            case SCENARIO_PARKING_STANDALONE:
                    productType = PRODUCT_TYPE_PARKING;
                    break;    
            case SCENARIO_STANDARD_WITH_EXTRAS:
                productType = PRODUCT_TYPE_TICKET;
                break;
            default:
                productType = PRODUCT_TYPE_TICKET;
        }

        return productType;
    }

    getSelectedProductGroups() {
        const productGroupsList = [];

        this.props.categoriesList.forEach((category) =>
            category.productGroups.forEach((productGroup) => {
                if (this.isProductGroupHasProductsInBasket(productGroup) || this.isProductGroupPreselected(productGroup)) {
                    productGroupsList.push(ObjectHelper.clone(productGroup));
                }
            }));

        return productGroupsList;
    }

    isProductGroupHasProductsInBasket(productGroup) {
        const currentScenarioProducts = productGroup.products.filter((product) =>
            product.type === this.currentProductType);

        // check if this product group have products of current scenario
        if (currentScenarioProducts.length > 0) {
            const basketProductGroups = this.props.basket.productGroups;
            if (basketProductGroups[productGroup.id]) {
                return currentScenarioProducts.some((product) =>
                    basketProductGroups[productGroup.id].products.some((basketProduct) =>
                        basketProduct.type === product.type &&
                        basketProduct.tag === product.tag));
            }
        }

        return false;
    }

    getPreselectedProductGroupId() {
        return UrlHelper.getParamFromLocation(PRODUCT_GROUP_ID_URL_PARAM_NAME, window.location);
    }

    clearPreselectedProductGroupId() {
        // todo should we remove it from query string?
        /*
        // we have to use global api to remove query parameter silently
        let url = UrlHelper.getFullUrlWithAppliedParams(window.location, { [PRODUCT_GROUP_ID_URL_PARAM_NAME]: null });
        window.history.replaceState({}, '', url);
        */
    }

    setHasPreselectedProductGroup() {
        const preselectedGroupId = this.getPreselectedProductGroupId();
        if (preselectedGroupId) {
            this.clearPreselectedProductGroupId();
            this.hasPreselectedProductGroup = this.props.categoriesList
                .some((category) =>
                    category.productGroups.some((productGroup) => productGroup.id.toLowerCase() === preselectedGroupId.toLowerCase()));
        } else {
            this.hasPreselectedProductGroup = false;
        }
    }

    isProductGroupPreselected(productGroup) {
        if (!productGroup.isSoldOut) {
            const hasCurrentScenarioProducts = productGroup.products.some((product) => product.type === this.currentProductType);
            const preselectedGroupId = this.getPreselectedProductGroupId();
            if (hasCurrentScenarioProducts && !isNullOrUndefined(preselectedGroupId)) {
                return preselectedGroupId.toLowerCase() === productGroup.id.toLowerCase();
            }
        }

        return false;
    }

    onSelectProductGroup(selProductGroup) {
        const basketProductGroups = ObjectHelper.clone(this.props.basket.productGroups);
        const productGroupsList = ObjectHelper.clone(this.state.productGroupsList);
        const productGroupIndex = ArrayHelper.findIndexByProp(
            productGroupsList,
            'id',
            selProductGroup.id
        );

        if (productGroupIndex === -1) {
            // if selProductGroup isn't in productGroupsList yet so we clicked to add it
            // in this case we want to reset all products of the products types that are active for current scenario
            // from all product groups in basket
            productGroupsList.forEach((productGroup) => {
                if (productGroup.id in basketProductGroups) {
                    this.resetProducts(basketProductGroups[productGroup.id], selProductGroup.products);
                }
            });
            productGroupsList.push(selProductGroup);
        } else {
            // otherwise we want to unselect some product group and remove it from basket
            // remove it from selected
            productGroupsList.splice(productGroupIndex, 1);
            // remove it from basket
            if (selProductGroup.id in basketProductGroups) {
                // remove all the current products from selected group in basket
                this.resetProducts(basketProductGroups[selProductGroup.id], selProductGroup.products);
            }
        }

        this.props.onProductGroupsUpdate(basketProductGroups);
        this.setState({ productGroupsList });
    }

    /**
     * Apply passed quantity to all appropriate products in selected days of the current scenario
     * So if you call it for example with {type: tickets, tag: adults} it will find particular products
     * by the passed params in all SELECTED groups and insert/update these groups and products in the basket
     *
     * @param productsParams
     * @param quantity
     */
    onApplyProductsQuantity(productsParams, quantity) {
        const basketProductGroups = ObjectHelper.clone(this.props.basket.productGroups);

        this.state.productGroupsList.forEach((productGroup) => {
            // filter products in selected product groups by passed set of keys/values
            const filteredProductsList = productGroup.products.filter((product) => Object.keys(productsParams).every((property) => {
                const value = productsParams[property];
                return product[property] === value;
            }));

            filteredProductsList.forEach((filteredProduct) => {
                // add product group to basket if it isn't there yet
                if (!basketProductGroups[productGroup.id]) {
                    basketProductGroups[productGroup.id] = ObjectHelper.clone(productGroup);
                    basketProductGroups[productGroup.id].products = [];
                }

                const basketProduct = basketProductGroups[productGroup.id].products.find((curBasketProduct) =>
                    curBasketProduct.type === filteredProduct.type &&
                    curBasketProduct.tag === filteredProduct.tag);

                if (basketProduct) {
                    if (basketProduct.tag === PRODUCT_TAG_TICKET_PLUS && quantity === 0) {
                        const basketProductExtra = basketProductGroups[productGroup.id].products.find((basketProduct) => basketProduct.tag === PRODUCT_TAG_TICKET_PLUS_EXTRA);
            
                        if (basketProductExtra) {
                            basketProductExtra.quantity = quantity;
                        }
                    }

                    basketProduct.quantity = quantity;
                } else {
                    const newProduct = ObjectHelper.clone(filteredProduct);
                    newProduct.quantity = quantity;
                    basketProductGroups[productGroup.id].products.push(newProduct);
                }
            });
        });

        this.props.onProductGroupsUpdate(basketProductGroups, ValidationHelper.isNeedResetJunior(productsParams.type, productsParams.tag));
    }

    onClearSelection(paramsList) {
        const basketProductGroups = ObjectHelper.clone(this.props.basket.productGroups);

        this.state.productGroupsList.forEach((productGroup) => {
            if (productGroup.id in basketProductGroups) {
                const basketProductGroup = basketProductGroups[productGroup.id];

                paramsList.forEach((param) => {
                    const basketProductIndex = basketProductGroup.products.findIndex((basketProduct) =>
                        basketProduct.type === param.type &&
                        basketProduct.tag === param.tag);

                    if (basketProductIndex !== -1) {
                        basketProductGroup.products.splice(basketProductIndex, 1);
                    }
                });
            }
        });

        this.props.onProductGroupsUpdate(basketProductGroups);
    }

    onToggleBasketMobile() {
        const isBasketExpanded = this.state.isBasketExpanded;

        if (isBasketExpanded) {
            contentBlocker.unblock('ticket-builder-basket');
            overlay.hide('ticket-builder-basket');
        } else {
            contentBlocker.block('ticket-builder-basket');
            overlay.show('ticket-builder-basket');
        }

        this.setState({ isBasketExpanded: !isBasketExpanded, isBasketToggling: true }, () => {
            setTimeout(() => {
                this.setState({ isBasketToggling: false });
            }, 300);
        });
    }

    resetProducts(basketProductGroup, products) {
        const removeTypesList = this.getRemoveTypesList();

        products.forEach((product) => {
            const shouldRemoveProduct = removeTypesList.some((type) => type === product.type);

            if (shouldRemoveProduct) {
                const basketProductIndex = basketProductGroup.products.findIndex((basketProduct) =>
                    basketProduct.type === product.type &&
                    basketProduct.tag === product.tag);

                if (basketProductIndex !== -1) {
                    basketProductGroup.products.splice(basketProductIndex, 1);
                }
            }
        });
    }

    getRemoveTypesList() {
        const removeTypesList = [];

        switch (this.props.currentScenario) {
            case SCENARIO_STANDARD:
                removeTypesList.push(PRODUCT_TYPE_TICKET, PRODUCT_TYPE_PARKING);
                break;
            case SCENARIO_HOSPITALITY:
            case SCENARIO_HOSPITALITY_WITH_EXTRAS:
                removeTypesList.push(PRODUCT_TYPE_HOSPITALITY, PRODUCT_TYPE_PARKING, PRODUCT_TYPE_EXTRAS);
                break;
            case SCENARIO_PARKING:
            case SCENARIO_PARKING_STANDALONE:
                removeTypesList.push(PRODUCT_TYPE_PARKING);
                break;
            case SCENARIO_STANDARD_WITH_EXTRAS:
                removeTypesList.push(PRODUCT_TYPE_TICKET, PRODUCT_TYPE_PARKING, PRODUCT_TYPE_EXTRAS);
                break;
        }

        return removeTypesList;
    }

    render() {
        return (
            <TicketBuilderContext.Provider value={this.tbContext}>
                <section
                    className={`ticket-builder ${this.state.isBasketExpanded ? 'is-basket-expanded' : ''} ${this.state.isBasketToggling ? 'is-basket-toggling' : ''}`}>
                    <Switch>
                        <Route
                            path={ROUTE_DAYS}
                            render={() =>
                                <SelectDaysPage
                                    dictionary={this.props.dictionary}
                                    basket={this.props.basket}
                                    productGroupsList={this.state.productGroupsList}
                                    categoriesList={this.props.categoriesList}
                                    currentScenario={this.props.currentScenario}
                                    onSelectProductGroup={this.onSelectProductGroup}
                                    onToggleBasketMobile={this.onToggleBasketMobile}
                                    history={this.props.history}
                                    hasPreselectedProductGroup={this.hasPreselectedProductGroup}
                                    basketUrl={this.props.basketUrl}
                                    isParkingStandaloneConfiguration={this.props.isParkingStandaloneConfiguration}
                                    isConfiguredForBallot={this.props.isConfiguredForBallot}
                                />
                            }
                        />
                        <Route
                            path={ROUTE_TICKETS}
                            render={() =>
                                <SelectTicketsPage
                                    dictionary={this.props.dictionary}
                                    basket={this.props.basket}
                                    productGroupsList={this.state.productGroupsList}
                                    categoriesList={this.props.categoriesList}
                                    currentScenario={this.props.currentScenario}
                                    onApplyProductsQuantity={this.onApplyProductsQuantity}
                                    onClearSelection={this.onClearSelection}
                                    onToggleBasketMobile={this.onToggleBasketMobile}
                                    onStartCheckout={this.props.onCheckout}
                                    basketUrl={this.props.basketUrl}
                                    isParkingStandaloneConfiguration={this.props.isParkingStandaloneConfiguration}
                                    isConfiguredForBallot={this.props.isConfiguredForBallot}
                                    quantityModifier={this.props.quantityModifier}
                                />
                            }
                        />
                        <Route
                            path={ROUTE_EXTRAS}
                            render={() =>
                                <SelectExtrasPage
                                    dictionary={this.props.dictionary}
                                    basket={this.props.basket}
                                    productGroupsList={this.state.productGroupsList}
                                    categoriesList={this.props.categoriesList}
                                    currentProductType={this.currentProductType}
                                    onApplyProductsQuantity={this.onApplyProductsQuantity}
                                    onUpdateProductQuantity={this.props.onUpdateProductQuantity}
                                    onClearSelection={this.onClearSelection}
                                    onToggleBasketMobile={this.onToggleBasketMobile}
                                    onStartCheckout={this.props.onCheckout}
                                    currentScenario={this.props.currentScenario}
                                    basketUrl={this.props.basketUrl}
                                    isParkingStandaloneConfiguration={this.props.isParkingStandaloneConfiguration}
                                    isConfiguredForBallot={this.props.isConfiguredForBallot}
                                />
                            }
                        />
                        <Route
                            path={ROUTE_PARKING}
                            render={() =>
                                <SelectParkingPage
                                    dictionary={this.props.dictionary}
                                    basket={this.props.basket}
                                    productGroupsList={this.state.productGroupsList}
                                    categoriesList={this.props.categoriesList}
                                    currentScenario={this.props.currentScenario}
                                    onApplyProductsQuantity={this.onApplyProductsQuantity}
                                    onClearSelection={this.onClearSelection}
                                    onToggleBasketMobile={this.onToggleBasketMobile}
                                    onStartCheckout={this.props.onCheckout}
                                    basketUrl={this.props.basketUrl}
                                    isConfiguredForBallot={this.props.isConfiguredForBallot}
                                    dialogBoxTitle={this.props.dialogBoxTitle}
                                    dialogBoxMessage={this.props.dialogBoxMessage}
                                    showDialogBox={this.props.showDialogBox}
                                    dialogBoxBanner={this.props.dialogBoxBanner}
                                />
                            }
                        />
                        <Route
                            path={ROUTE_PARKING_STANDALONE}
                            render={() =>
                                <SelectParkingStandalonePage
                                    dictionary={this.props.dictionary}
                                    basket={this.props.basket}
                                    productGroupsList={this.state.productGroupsList}
                                    categoriesList={this.props.categoriesList}
                                    currentProductType={this.currentProductType}
                                    onApplyProductsQuantity={this.onApplyProductsQuantity}
                                    onUpdateProductQuantity={this.props.onUpdateProductQuantity}
                                    onClearSelection={this.onClearSelection}
                                    onToggleBasketMobile={this.onToggleBasketMobile}
                                    onStartCheckout={this.props.onCheckout}
                                    currentScenario={this.props.currentScenario}
                                    basketUrl={this.props.basketUrl}
                                    isConfiguredForBallot={this.props.isConfiguredForBallot}
                                    dialogBoxTitle={this.props.dialogBoxTitle}
                                    dialogBoxMessage={this.props.dialogBoxMessage}
                                    dialogBoxLogo={this.props.dialogBoxLogo}
                                    dialogBoxBanner={this.props.dialogBoxBanner}
                                    showDialogBox={this.props.showDialogBox}
                                />
                            }
                        />
                        <Route render={() => (
                            <Redirect to={ROUTE_DAYS} />
                        )} />
                    </Switch>
                </section>
            </TicketBuilderContext.Provider>
        );
    }
}

TicketBuilder.propTypes = {
    categoriesList: PropTypes.array,
    currentScenario: PropTypes.string.isRequired,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    dictionary: PropTypes.object,
    basket: PropTypes.object,
    onProductGroupsUpdate: PropTypes.func,
    onUpdateProductQuantity: PropTypes.func,
    onCheckout: PropTypes.func,
    isParkingStandaloneConfiguration: PropTypes.bool,
    isConfiguredForBallot: PropTypes.bool,
    dialogBoxTitle: PropTypes.string,
    dialogBoxMessage: PropTypes.string,
    dialogBoxLogo: PropTypes.string,
    showDialogBox: PropTypes.bool
};


const TicketBuilderWithRouter = withRouter(TicketBuilder);
const TicketBuilderWithHashRouter = (props) => (
    <HashRouter>
        <TicketBuilderWithRouter {...props} />
    </HashRouter>
);

export default TicketBuilderWithHashRouter;
